import { runConstraintPipeline } from '../../core/constraint-pipeline.js';
import type { TimeAxisMath } from '../../core/time-axis-math.js';
import type { BarState, ConstraintIntent } from '../../extensions/types.js';
import type { GanttSingleView } from '../../gantt-view-manager.js';
import type { DragHandler } from './drag-handlers.js';
import type { GanttDragTooltip } from './drag-tooltip.js';

/** Pointer movement below this threshold (px) counts as a click. */
export const CLICK_THRESHOLD_PX = 3;

export interface DragCommitSuccess {
  readonly rowId: string;
  readonly startMs: number;
  readonly endMs: number;
}

export interface DragSessionConfig {
  readonly view: GanttSingleView;
  readonly math: TimeAxisMath;
  readonly handler: DragHandler;
  readonly tooltip: GanttDragTooltip | undefined;
  readonly target: HTMLElement;
  readonly pointerEvent: PointerEvent;
  /**
   * Converts viewport/client-space horizontal movement into the timeline's
   * local CSS-pixel movement. Edgeless canvas nodes are CSS-transformed by
   * zoom, so client deltas must be unscaled before date math runs.
   */
  readonly clientDeltaToTimelineDelta?:
    | ((clientDeltaX: number) => number)
    | undefined;
  /** Called on pointerup when the pointer never crossed the click threshold. */
  readonly onClick?: (() => void) | undefined;
  /**
   * Persists the committed state. Defaults to writing start/end cells
   * via `valueSet` (creating the row first for `create` intent). Tests
   * inject a spy.
   */
  readonly commit?: ((state: BarState) => void) | undefined;
  /**
   * Invoked on every pointermove with the running post-constraint state
   * (or `null` when the proposal was rejected). Used by the renderer to
   * show the bar at its proposed position during the drag without
   * touching Y.js.
   */
  readonly onPreview?: ((state: BarState | null) => void) | undefined;
}

/**
 * Owns a single pointerdown→pointerup lifecycle for bar drag.
 *
 * Responsibilities:
 *  - Capture the pointer so Edgeless pan-tool never sees the events.
 *  - Stop propagation + preventDefault on every pointer event.
 *  - Track the click threshold (small movement → click, not drag).
 *  - Run each proposed state through the registered
 *    {@link runConstraintPipeline} on every pointermove.
 *  - Update the drag tooltip with the running state or warning.
 *  - Commit via Y.js writes on pointerup (only if threshold crossed).
 *
 * Instantiate via {@link startDragSession} — the constructor wires up
 * listeners and returns. The session tears itself down on pointerup or
 * pointercancel.
 */
export class DragSession {
  private readonly startClientX: number;
  private readonly startClientY: number;
  private movedBeyondThreshold = false;
  private pendingState: BarState | null = null;
  private readonly capturedPointerId: number;
  private readonly removeListeners: () => void;

  constructor(private readonly cfg: DragSessionConfig) {
    this.startClientX = cfg.pointerEvent.clientX;
    this.startClientY = cfg.pointerEvent.clientY;
    this.capturedPointerId = cfg.pointerEvent.pointerId;

    cfg.pointerEvent.preventDefault();
    cfg.pointerEvent.stopPropagation();

    try {
      cfg.target.setPointerCapture(this.capturedPointerId);
    } catch {
      // Pointer capture fails when the element was detached mid-event.
      // We still rely on event.stopPropagation above to block Edgeless
      // pan, so the session degrades gracefully without capture.
    }

    const onMove = (evt: PointerEvent) => this.handleMove(evt);
    const onUp = (evt: PointerEvent) => this.handleUp(evt);
    const onCancel = (evt: PointerEvent) => this.handleCancel(evt);

    cfg.target.addEventListener('pointermove', onMove);
    cfg.target.addEventListener('pointerup', onUp);
    cfg.target.addEventListener('pointercancel', onCancel);

    this.removeListeners = () => {
      cfg.target.removeEventListener('pointermove', onMove);
      cfg.target.removeEventListener('pointerup', onUp);
      cfg.target.removeEventListener('pointercancel', onCancel);
    };
  }

  get intent(): ConstraintIntent {
    return this.cfg.handler.intent;
  }

  private handleMove(evt: PointerEvent): void {
    if (evt.pointerId !== this.capturedPointerId) return;
    evt.preventDefault();
    evt.stopPropagation();

    const dx = evt.clientX - this.startClientX;
    const dy = evt.clientY - this.startClientY;
    if (!this.movedBeyondThreshold) {
      if (Math.hypot(dx, dy) < CLICK_THRESHOLD_PX) return;
      this.movedBeyondThreshold = true;
    }

    const timelineDx = this.cfg.clientDeltaToTimelineDelta?.(dx) ?? dx;
    const deltaMs = this.cfg.math.widthToDuration(timelineDx);
    const proposed = this.cfg.handler.compute(deltaMs);
    const result = runConstraintPipeline(
      proposed,
      this.cfg.view.extensions.dragConstraints.list(),
      {
        view: this.cfg.view,
        intent: this.cfg.handler.intent,
        previousState: this.cfg.handler.initialState,
      }
    );

    if (!result.allowed) {
      this.pendingState = null;
      this.cfg.onPreview?.(null);
      this.cfg.tooltip?.show({
        clientX: evt.clientX,
        clientY: evt.clientY,
        startMs: proposed.startMs,
        endMs: proposed.endMs,
        warning: result.warning,
      });
      return;
    }

    this.pendingState = result.state;
    this.cfg.onPreview?.(result.state);
    this.cfg.tooltip?.show({
      clientX: evt.clientX,
      clientY: evt.clientY,
      startMs: result.state.startMs,
      endMs: result.state.endMs,
      warning: result.warnings[0],
    });
  }

  private handleUp(evt: PointerEvent): void {
    if (evt.pointerId !== this.capturedPointerId) return;
    evt.preventDefault();
    evt.stopPropagation();
    this.cleanup();

    if (!this.movedBeyondThreshold) {
      this.cfg.onClick?.();
      return;
    }

    const finalState = this.pendingState;
    if (!finalState) return;

    if (this.cfg.commit) {
      this.cfg.commit(finalState);
    } else {
      defaultCommit(this.cfg.view, finalState, this.cfg.handler.intent);
    }
  }

  private handleCancel(evt: PointerEvent): void {
    if (evt.pointerId !== this.capturedPointerId) return;
    this.cleanup();
  }

  private cleanup(): void {
    try {
      this.cfg.target.releasePointerCapture(this.capturedPointerId);
    } catch {
      // Already released; safe to ignore.
    }
    this.removeListeners();
    this.cfg.tooltip?.hide();
    this.cfg.onPreview?.(null);
  }
}

/**
 * Default persistence path. For `create` we first insert a new row at
 * the end of the view, then set start/end cells on the fresh rowId.
 * For the other intents we write both cells so that constraint
 * adjustments (e.g. clamp) land atomically-from-the-user's-POV in Y.js.
 */
function defaultCommit(
  view: GanttSingleView,
  state: BarState,
  intent: ConstraintIntent
): void {
  const startCol = view.startDateCol$.value;
  const endCol = view.endDateCol$.value;
  if (!startCol || !endCol) return;

  let rowId = state.rowId;
  if (intent === 'create') {
    const newRowId = view.rowAdd('end');
    if (!newRowId) return;
    rowId = newRowId;
  }

  view.propertyGetOrCreate(startCol).valueSet(rowId, state.startMs);
  view.propertyGetOrCreate(endCol).valueSet(rowId, state.endMs);
}

export function startDragSession(cfg: DragSessionConfig): DragSession {
  return new DragSession(cfg);
}
